import { FC, useState, useMemo } from 'react'
import { Button } from '@components/ui'
import s from './ProductInfo.module.css'
import { addItemToCart } from '@lib/shopify'
import mixpanel from '@lib/mixpanel'
import { useCartContext } from '@components/cart/context'
import { ShopifyProductMetafield, ShopifyProductVariant } from '@types'
import Select, { components, OptionProps, SingleValueProps } from 'react-select'
import { useTranslation } from 'next-i18next'
import { formatPrice } from '@lib/pricing'
import { useRouter } from 'next/router'
import Link from 'next/link'

interface ProductInfoProps {
  title: string
  description: string
  price: number
  sizes: Array<string>
  variants: ShopifyProductVariant[]
  sizeChart?: ShopifyProductMetafield | undefined
  currencyCode: string
  handle: string
}

type SizeOption = {
  value: string
  label: string
  available: boolean
}

const SelectOption = ({ children, data, ...rest }: OptionProps<SizeOption>) => {
  const { t } = useTranslation(['common'])

  return (
    // @ts-ignore
    <components.Option {...rest}>
      {children}
      {!data.available && (
        <span className="text-sm text-rose-600 italic">
          {' '}
          {t('common:outOfStock.titleInParenthesis')}
        </span>
      )}
    </components.Option>
  )
}

const ProductGallery: FC<ProductInfoProps> = ({
  title,
  description,
  price,
  sizes,
  variants,
  sizeChart,
  currencyCode,
  handle,
}) => {
  const { t } = useTranslation(['common'])
  const { locale } = useRouter()
  const { setCartItems, setIsCartOpened, setTotalPrice, setCheckoutUrl } =
    useCartContext()

  const [quantity, setQuantity] = useState(1)
  const [multipliedPrice, setMultipliedPrice] = useState(price)
  const [isAddingItemIntoCart, setIsAddingItemIntoCart] = useState(false)

  const areVariantsOrChartVisible =
    sizes.length > 1 || sizeChart?.value.toLowerCase() === 'true'

  const formattedSizes = sizes.map((s) => {
    const variant = variants.find((v) => v.selectedOptions[0].value === s)

    return {
      value: s,
      label: s,
      available: variant?.availableForSale || false,
    }
  })

  const [selectedSize, setSelectedSize] = useState(formattedSizes[0])
  const [selectedVariant, setSelectedVariant] = useState(variants[0])

  const updateQuantity = (value: number) => {
    if (value < 1) {
      return setQuantity(1)
    }

    if (value > 99) {
      return setQuantity(99)
    }

    setQuantity(value)
  }

  useMemo(() => {
    setMultipliedPrice(quantity * price)
  }, [quantity, setMultipliedPrice, price])

  useMemo(() => {
    const variant = variants.find(
      (v) => v.selectedOptions[0].value === selectedSize.value
    )

    if (variant) {
      setSelectedVariant(variant)
    }
  }, [selectedSize, variants])

  const addToCart = async (): Promise<void> => {
    setIsAddingItemIntoCart(true)

    const res = await addItemToCart(
      { quantity, merchandiseId: selectedVariant.id },
      locale || 'en-US'
    )

    setCartItems(res.cart.lines.edges.map((e: any) => e.node))
    setIsCartOpened(true)
    setTotalPrice(res.cart.estimatedCost.totalAmount)
    setCheckoutUrl(res.cart.checkoutUrl)
    setIsAddingItemIntoCart(false)

    if (process.env.NODE_ENV === 'production') {
      mixpanel.track('Added to cart', {
        productHandle: handle,
        productTitle: title,
        productQuantity: quantity,
      })

      return
    }

    console.info('Track product added to cart', {
      productHandle: handle,
      productTitle: title,
      productQuantity: quantity,
    })
  }

  return (
    <div>
      <h1 className="text-3xl md:text-4xl lg:text-5xl mb-5">{title}</h1>
      <div dangerouslySetInnerHTML={{ __html: description }} />
      {areVariantsOrChartVisible && (
        <div className="flex items-center gap-8 mt-10">
          {sizes.length > 1 && (
            <Select
              className="flex-1"
              options={formattedSizes}
              isDisabled={formattedSizes.length < 2}
              isSearchable={false}
              value={selectedSize}
              // @ts-ignore
              onChange={(option) => setSelectedSize(option!)}
              components={{ Option: SelectOption }}
            />
          )}
          {sizeChart?.value.toLowerCase() === 'true' && (
            <Link href="/size-chart" passHref>
              <Button Component="a" variant="naked" target="_blank">
                <span className="text-black">
                  {t('common:labels.sizeChart')}
                </span>
              </Button>
            </Link>
          )}
        </div>
      )}
      <div className="flex gap-4 lg:gap-8 items-center mt-10 justify-between">
        <p className="text-2xl md:text-3xl lg:text-4xl">
          <strong>
            {t(`common:price.${currencyCode}`, {
              price: formatPrice(locale || 'en-US', multipliedPrice),
            })}
          </strong>
        </p>
        {selectedVariant.availableForSale && (
          <div className="flex">
            <Button
              variant="naked"
              className={s.amountBtn}
              onClick={() => updateQuantity(quantity - 1)}
            >
              -
            </Button>
            <input
              type="number"
              value={quantity}
              className={s.amountInput}
              onChange={(e) => updateQuantity(parseInt(e.target.value, 10))}
              min="1"
              max="99"
            />
            <Button
              variant="naked"
              className={s.amountBtn}
              onClick={() => updateQuantity(quantity + 1)}
            >
              +
            </Button>
          </div>
        )}
      </div>
      <div className="flex gap-4 mt-8">
        {selectedVariant.availableForSale ? (
          <Button onClick={addToCart} loading={isAddingItemIntoCart}>
            {t('common:labels.addToShoppingCart')}
          </Button>
        ) : (
          <div>
            <h3 className="text-xl text-rose-600">
              {t('common:outOfStock.title')}
            </h3>
            <p className="mt-2">{t('common:outOfStock.message')}</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default ProductGallery
